import { useMemo } from 'react';
import { 
  panchangData, 
  sunAndMoonData, 
  auspiciousTimingsData, 
  inauspiciousTimingsData, 
  dateSpecificFestivalData 
} from '../config/mockData.config';

/**
 * Hook to transform complex Panchang data into structured, easy-to-use arrays
 * @param {Object} options - Configuration options
 * @param {boolean} options.includeEmptyArrays - Include empty arrays in output (default: false)
 * @param {string} options.dateFormat - Date format for display (default: 'DD-MM-YYYY HH:mm')
 * @param {boolean} options.includeRawData - Include original raw data (default: false)
 * @returns {Object} Structured Panchang data
 */
export function usePanchangDataMapper(options = {}) {
  const {
    includeEmptyArrays = false,
    dateFormat = 'DD-MM-YYYY HH:mm',
    includeRawData = false
  } = options;

  const structuredData = useMemo(() => {
    return {
      // Basic Panchang Information
      basicInfo: transformBasicInfo(panchangData, dateFormat),
      
      // Sun and Moon Data
      sunMoon: transformSunMoonData(sunAndMoonData, dateFormat),
      
      // Nakshatras (Star Constellations)
      nakshatras: transformNakshatras(panchangData, includeEmptyArrays, dateFormat),
      
      // Tithis (Lunar Days)
      tithis: transformTithis(panchangData, dateFormat),
      
      // Yogas (Auspicious Combinations)
      yogas: transformYogas(panchangData, includeEmptyArrays, dateFormat),
      
      // Karnas (Half Tithis)
      karnas: transformKarnas(panchangData, dateFormat),
      
      // Auspicious Timings
      auspiciousTimings: transformAuspiciousTimings(auspiciousTimingsData, includeEmptyArrays, dateFormat),
      
      // Inauspicious Timings
      inauspiciousTimings: transformInauspiciousTimings(inauspiciousTimingsData, includeEmptyArrays, dateFormat),
      
      // Festivals and Special Events
      festivals: transformFestivals(dateSpecificFestivalData, dateFormat),
      
      // Summary for quick access
      summary: generateSummary(panchangData, sunAndMoonData, auspiciousTimingsData, inauspiciousTimingsData),
      
      // Raw data (if requested)
      ...(includeRawData && {
        raw: {
          panchang: panchangData,
          sunMoon: sunAndMoonData,
          auspicious: auspiciousTimingsData,
          inauspicious: inauspiciousTimingsData,
          festivals: dateSpecificFestivalData
        }
      })
    };
  }, [includeEmptyArrays, dateFormat, includeRawData]);

  return structuredData;
}

// Helper Functions

/**
 * Transform basic Panchang information
 */
function transformBasicInfo(data, dateFormat) {
  return {
    sunrise: data.data?.sunrise || '',
    sunset: data.data?.sunset || '',
    date: extractDate(data.data?.sunrise) || new Date().toISOString().split('T')[0],
    success: data.success === 1
  };
}

/**
 * Transform Sun and Moon data
 */
function transformSunMoonData(data, dateFormat) {
  return {
    sunrise: data.data?.sunrise || '',
    sunset: data.data?.sunset || '',
    moonrise: data.data?.moonrise || '',
    moonset: data.data?.moonset || '',
    weekday: data.data?.weekday || '',
    success: data.success === 1
  };
}

/**
 * Transform Nakshatras data into structured arrays
 */
function transformNakshatras(data, includeEmptyArrays, dateFormat) {
  const nakshatras = data.data?.nakshatras;
  if (!nakshatras) return { zodiacPoints: [], nakshatraList: [], nakshatraPadas: [] };

  return {
    zodiacPoints: nakshatras.zodiac_point?.map((point, index) => ({
      id: index + 1,
      sign: point.sign || '',
      endTime: point.end_time || '',
      endTimeFormatted: formatDateTime(point.end_time, dateFormat),
      isEmpty: !point.end_time
    })) || [],
    
    nakshatraList: nakshatras.nakshatra_list?.map((nak, index) => ({
      id: index + 1,
      name: nak.nak_name || '',
      endTime: nak.end_time || '',
      endTimeFormatted: formatDateTime(nak.end_time, dateFormat),
      isEmpty: !nak.end_time
    })) || [],
    
    nakshatraPadas: nakshatras.nakshatra_pada?.map((pada, index) => ({
      id: index + 1,
      name: pada.nak_name || '',
      lord: pada.lord || '',
      deity: pada.deity || '',
      element: pada.element || '',
      symbol: pada.symbol || '',
      syllables: pada.syllables || '',
      pursuit: pada.pursuit || '',
      gana: pada.gana || '',
      direction: pada.direction || '',
      guna: pada.guna || '',
      bodyPart: pada.body_part || '',
      color: pada.color || '',
      nakNumber: pada.nak_number || 0,
      nakPada: pada.nak_pada || 0,
      endTime: pada.end_time || '',
      endTimeFormatted: formatDateTime(pada.end_time, dateFormat),
      endTimeSeparated: pada.end_time_seprated ? {
        day: pada.end_time_seprated.day || '',
        month: pada.end_time_seprated.month || '',
        year: pada.end_time_seprated.year || '',
        hour: pada.end_time_seprated.hour || '',
        minute: pada.end_time_seprated.minute || '',
        second: pada.end_time_seprated.second || ''
      } : null,
      isEmpty: !pada.end_time
    })) || []
  };
}

/**
 * Transform Tithis data
 */
function transformTithis(data, dateFormat) {
  return data.data?.tithis?.map((tithi, index) => ({
    id: index + 1,
    number: tithi.number || '',
    name: tithi.tithi || '',
    paksha: tithi.paksha || '',
    type: tithi.type || '',
    deity: tithi.deity || '',
    startTime: tithi.start_time || '',
    endTime: tithi.end_time || '',
    startTimeFormatted: formatDateTime(tithi.start_time, dateFormat),
    endTimeFormatted: formatDateTime(tithi.end_time, dateFormat),
    vriddhi: tithi.vriddhi === 'true',
    kshaya: tithi.kshaya === 'true',
    gandanta: tithi.gandanta ? {
      startTime: tithi.gandanta.start_time || '',
      endTime: tithi.gandanta.end_time || '',
      startTimeFormatted: formatDateTime(tithi.gandanta.start_time, dateFormat),
      endTimeFormatted: formatDateTime(tithi.gandanta.end_time, dateFormat)
    } : null,
    pakshaRandhra: tithi.paksha_randhra ? {
      startTime: tithi.paksha_randhra.start_time || '',
      endTime: tithi.paksha_randhra.end_time || '',
      startTimeFormatted: formatDateTime(tithi.paksha_randhra.start_time, dateFormat),
      endTimeFormatted: formatDateTime(tithi.paksha_randhra.end_time, dateFormat)
    } : null
  })) || [];
}

/**
 * Transform Yogas data
 */
function transformYogas(data, includeEmptyArrays, dateFormat) {
  return data.data?.yogas?.map((yoga, index) => ({
    id: index + 1,
    number: yoga.yoga_number || '',
    name: yoga.yoga_name || '',
    startTime: yoga.start_time || '',
    endTime: yoga.end_time || '',
    startTimeFormatted: formatDateTime(yoga.start_time, dateFormat),
    endTimeFormatted: formatDateTime(yoga.end_time, dateFormat),
    vishaGhatis: yoga.visha_ghatis ? {
      startTime: yoga.visha_ghatis.start_time || '',
      endTime: yoga.visha_ghatis.end_time || '',
      startTimeFormatted: formatDateTime(yoga.visha_ghatis.start_time, dateFormat),
      endTimeFormatted: formatDateTime(yoga.visha_ghatis.end_time, dateFormat)
    } : null,
    hasVishaGhatis: !!(yoga.visha_ghatis && yoga.visha_ghatis.start_time)
  })) || [];
}

/**
 * Transform Karnas data
 */
function transformKarnas(data, dateFormat) {
  return data.data?.karnas?.map((karana, index) => ({
    id: index + 1,
    name: karana.karana_name || '',
    tithi: karana.tithi || '',
    paksha: karana.paksha || '',
    mobility: karana.mobility || '',
    rulingPlanet: karana.rulling_planet || '',
    devata: karana.devata || '',
    nature: karana.nature || '',
    startTime: karana.start_time || '',
    endTime: karana.end_time || '',
    startTimeFormatted: formatDateTime(karana.start_time, dateFormat),
    endTimeFormatted: formatDateTime(karana.end_time, dateFormat),
    bhadra: karana.bhadra || [],
    bhadraNiwas: karana.bhadra_niwas || [],
    hasBhadra: !!(karana.bhadra && karana.bhadra.length > 0)
  })) || [];
}

/**
 * Transform Auspicious Timings data with dynamic keys
 */
function transformAuspiciousTimings(data, includeEmptyArrays, dateFormat) {
  const timings = data.data;
  if (!timings) return {};

  const result = {
    date: timings.date || '',
    sunrise: timings.sunrise || '',
    sunset: timings.sunset || '',
    sunriseFormatted: formatDateTime(timings.sunrise, dateFormat),
    sunsetFormatted: formatDateTime(timings.sunset, dateFormat),
    // Dynamic timings will be added below
    timings: {},
    arrays: {}
  };

  // Process all dynamic keys
  Object.keys(timings).forEach(key => {
    // Skip basic fields we already handled
    if (['date', 'sunrise', 'sunset'].includes(key)) return;

    const value = timings[key];
    
    if (Array.isArray(value)) {
      // Handle array values
      if (value.length > 0) {
        result.arrays[key] = value.map((item, index) => {
          if (typeof item === 'object' && item !== null) {
            return {
              id: index + 1,
              ...transformTimingObject(item, dateFormat)
            };
          }
          return item;
        });
      } else if (includeEmptyArrays) {
        result.arrays[key] = [];
      }
    } else if (typeof value === 'object' && value !== null) {
      // Handle object values (timing objects)
      result.timings[key] = transformTimingObject(value, dateFormat);
    } else {
      // Handle primitive values
      result[key] = value;
    }
  });

  // Filter out empty arrays if not requested
  if (!includeEmptyArrays) {
    Object.keys(result.arrays).forEach(key => {
      if (result.arrays[key].length === 0) {
        delete result.arrays[key];
      }
    });
  }

  return result;
}

/**
 * Transform Inauspicious Timings data with dynamic keys
 */
function transformInauspiciousTimings(data, includeEmptyArrays, dateFormat) {
  const timings = data.data;
  if (!timings) return {};

  const result = {
    date: timings.date || '',
    sunrise: timings.sunrise || '',
    sunset: timings.sunset || '',
    sunriseFormatted: formatDateTime(timings.sunrise, dateFormat),
    sunsetFormatted: formatDateTime(timings.sunset, dateFormat),
    // Dynamic timings will be added below
    timings: {},
    arrays: {}
  };

  // Process all dynamic keys
  Object.keys(timings).forEach(key => {
    // Skip basic fields we already handled
    if (['date', 'sunrise', 'sunset'].includes(key)) return;

    const value = timings[key];
    
    if (Array.isArray(value)) {
      // Handle array values
      if (value.length > 0) {
        result.arrays[key] = value.map((item, index) => {
          if (typeof item === 'object' && item !== null) {
            return {
              id: index + 1,
              ...transformTimingObject(item, dateFormat)
            };
          }
          return item;
        });
      } else if (includeEmptyArrays) {
        result.arrays[key] = [];
      }
    } else if (typeof value === 'object' && value !== null) {
      // Handle object values (timing objects)
      result.timings[key] = transformTimingObject(value, dateFormat);
    } else {
      // Handle primitive values
      result[key] = value;
    }
  });

  // Filter out empty arrays if not requested
  if (!includeEmptyArrays) {
    Object.keys(result.arrays).forEach(key => {
      if (result.arrays[key].length === 0) {
        delete result.arrays[key];
      }
    });
  }

  return result;
}

/**
 * Transform Festivals data with dynamic keys
 */
function transformFestivals(data, dateFormat) {
  const festivals = data.data;
  if (!festivals) return {};

  const result = {
    // Dynamic festivals will be added below
    festivals: {}
  };

  // Process all dynamic festival keys
  Object.keys(festivals).forEach(festivalKey => {
    const festival = festivals[festivalKey];
    
    if (typeof festival === 'object' && festival !== null) {
      // Check if it's a multi-day festival (like Navaratri) with sub-days
      const hasSubDays = Object.values(festival).some(value => 
        typeof value === 'object' && value !== null && value.date
      );
      
      if (hasSubDays) {
        // Multi-day festival (e.g., Navaratri)
        result.festivals[festivalKey] = {};
        Object.keys(festival).forEach(dayKey => {
          const day = festival[dayKey];
          if (typeof day === 'object' && day !== null) {
            result.festivals[festivalKey][dayKey] = transformFestivalDay(day, dateFormat);
          }
        });
      } else {
        // Single festival
        result.festivals[festivalKey] = transformFestivalDay(festival, dateFormat);
      }
    }
  });

  return result;
}

/**
 * Transform timing object (used for both auspicious and inauspicious timings)
 */
function transformTimingObject(timingObj, dateFormat) {
  if (!timingObj || typeof timingObj !== 'object') return timingObj;

  const result = { ...timingObj };

  // Transform common timing fields
  if (timingObj.start_time) {
    result.startTime = timingObj.start_time;
    result.startTimeFormatted = formatDateTime(timingObj.start_time, dateFormat);
  }
  
  if (timingObj.end_time) {
    result.endTime = timingObj.end_time;
    result.endTimeFormatted = formatDateTime(timingObj.end_time, dateFormat);
  }

  // Process nested objects recursively
  Object.keys(timingObj).forEach(key => {
    if (key !== 'start_time' && key !== 'end_time' && 
        typeof timingObj[key] === 'object' && timingObj[key] !== null) {
      if (Array.isArray(timingObj[key])) {
        result[key] = timingObj[key].map((item, index) => {
          if (typeof item === 'object' && item !== null) {
            return {
              id: index + 1,
              ...transformTimingObject(item, dateFormat)
            };
          }
          return item;
        });
      } else {
        result[key] = transformTimingObject(timingObj[key], dateFormat);
      }
    }
  });

  return result;
}

/**
 * Transform festival day (generic function for any festival day)
 */
function transformFestivalDay(day, dateFormat) {
  if (!day || typeof day !== 'object') return day;

  const result = { ...day };

  // Process all properties of the festival day
  Object.keys(day).forEach(key => {
    const value = day[key];
    
    if (Array.isArray(value)) {
      // Handle arrays (like muhurat arrays)
      result[key] = value.map((item, index) => {
        if (Array.isArray(item)) {
          // Handle array of arrays (like ghatasthapana_muhurat)
          return {
            id: index + 1,
            startTime: item[0] || '',
            endTime: item[1] || '',
            startTimeFormatted: formatDateTime(item[0], dateFormat),
            endTimeFormatted: formatDateTime(item[1], dateFormat)
          };
        } else if (typeof item === 'object' && item !== null) {
          return {
            id: index + 1,
            ...transformTimingObject(item, dateFormat)
          };
        }
        return item;
      });
    } else if (typeof value === 'object' && value !== null) {
      // Handle objects (like parana, ghatasthapana_abhijit_muhurat)
      result[key] = transformTimingObject(value, dateFormat);
    }
  });

  return result;
}

/**
 * Transform individual Navaratri day (legacy function - kept for backward compatibility)
 */
function transformNavaratriDay(day, dateFormat) {
  return transformFestivalDay(day, dateFormat);
}

/**
 * Generate summary for quick access
 */
function generateSummary(panchangData, sunMoonData, auspiciousData, inauspiciousData) {
  // Helper function to check if any timing exists
  const hasAnyTiming = (timings) => {
    if (!timings || !timings.data) return false;
    return Object.keys(timings.data).some(key => {
      const value = timings.data[key];
      return (typeof value === 'object' && value !== null && value.start_time) ||
             (Array.isArray(value) && value.length > 0);
    });
  };

  return {
    currentTithi: panchangData.data?.tithis?.[0]?.tithi || 'Unknown',
    currentNakshatra: panchangData.data?.nakshatras?.nakshatra_pada?.[0]?.nak_name || 'Unknown',
    currentYoga: panchangData.data?.yogas?.[0]?.yoga_name || 'Unknown',
    currentKarana: panchangData.data?.karnas?.[0]?.karana_name || 'Unknown',
    sunrise: sunMoonData.data?.sunrise || '',
    sunset: sunMoonData.data?.sunset || '',
    moonrise: sunMoonData.data?.moonrise || '',
    moonset: sunMoonData.data?.moonset || '',
    weekday: sunMoonData.data?.weekday || 'Unknown',
    hasAuspiciousTimings: hasAnyTiming(auspiciousData),
    hasInauspiciousTimings: hasAnyTiming(inauspiciousData),
    totalTithis: panchangData.data?.tithis?.length || 0,
    totalNakshatras: panchangData.data?.nakshatras?.nakshatra_pada?.length || 0,
    totalYogas: panchangData.data?.yogas?.length || 0,
    totalKarnas: panchangData.data?.karnas?.length || 0,
    // Dynamic counts
    totalAuspiciousTimings: auspiciousData.data ? Object.keys(auspiciousData.data).length - 3 : 0, // -3 for date, sunrise, sunset
    totalInauspiciousTimings: inauspiciousData.data ? Object.keys(inauspiciousData.data).length - 3 : 0,
    totalFestivals: 0 // Will be calculated dynamically
  };
}

/**
 * Format datetime string
 */
function formatDateTime(dateTimeString, format) {
  if (!dateTimeString) return '';
  
  try {
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) return dateTimeString;
    
    // Simple formatting - can be enhanced with date-fns or moment.js
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return format
      .replace('DD', day)
      .replace('MM', month)
      .replace('YYYY', year)
      .replace('HH', hours)
      .replace('mm', minutes);
  } catch (error) {
    return dateTimeString;
  }
}

/**
 * Extract date from datetime string
 */
function extractDate(dateTimeString) {
  if (!dateTimeString) return null;
  try {
    return new Date(dateTimeString).toISOString().split('T')[0];
  } catch (error) {
    return null;
  }
}

export default usePanchangDataMapper;
